import React, { useEffect, useState, useCallback } from "react";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const GREEN = "#16A34A";
const RED = "#DC2626";

const SOURCE_LABEL = {
  file: "From phone cache",
  proxy: "Head-start cache",
  network: "Streamed small copy",
  hls: "Streamed (HLS fallback)",
  raw: "Original file",
  "": "Unknown",
};

const ms = (v) => (v == null ? "—" : v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(1)} s`);

// How fast reels start and how often they stall, measured on users' phones
// (2026-10-05). Goal: most reels start "instantly" (≤ 0.3 s) with no stalls.
// "GH" -> "Ghana"; older browsers just show the code.
const countryName = (code) => {
  if (!code) return "Not set";
  try {
    return `${new Intl.DisplayNames(["en"], { type: "region" }).of(code)} (${code})`;
  } catch (e) {
    return code;
  }
};

const VideoPlayback = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiInstanceFetch.get(`reelPlayback/admin/summary?days=${days}`);
      if (res && res.status) setData(res);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data && data.total;

  const card = (label, value, sub, color) => (
    <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 14, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || INK, marginTop: 4 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );

  const th = { textAlign: "left", padding: "9px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIRLINE}`, whiteSpace: "nowrap" };
  const td = { padding: "9px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIRLINE}`, whiteSpace: "nowrap" };

  const table = (title, rows, labelFn) => (
    <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, marginTop: 16, overflow: "auto" }}>
      <div style={{ padding: "12px 14px", fontWeight: 700, color: INK }}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead>
          <tr>
            <th style={th}></th>
            <th style={th}>Views</th>
            <th style={th}>Typical start</th>
            <th style={th}>Slowest 25%</th>
            <th style={th}>Slowest 10%</th>
            <th style={th}>Instant (≤0.3s)</th>
            <th style={th}>Views with a stall</th>
            <th style={th}>Time spent buffering</th>
            <th style={th}>Left before it started</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 ? (
            <tr>
              <td style={{ ...td, color: MUTED }} colSpan={9}>
                No data yet — reports arrive once users have the new app build.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={String(r.key)}>
                <td style={{ ...td, fontWeight: 600 }}>{labelFn ? labelFn(r.key) : String(r.key || "—")}</td>
                <td style={td}>{r.views.toLocaleString()}</td>
                <td style={td}>{ms(r.startP50)}</td>
                <td style={td}>{ms(r.startP75)}</td>
                <td style={td}>{ms(r.startP90)}</td>
                <td style={{ ...td, color: r.instantPct >= 75 ? GREEN : INK, fontWeight: 600 }}>{r.instantPct}%</td>
                <td style={{ ...td, color: r.stallViewsPct > 10 ? RED : INK }}>{r.stallViewsPct}%</td>
                <td style={td}>{r.bufferingSharePct}%</td>
                <td style={td}>{r.abandonedPct}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      <Title name="Video Playback" />
      <p style={{ color: MUTED, fontSize: 13, margin: "6px 0 12px" }}>
        How fast reels start after they appear on screen, and how often they pause to buffer — measured on users' phones.
        Target: at least 75% of views start instantly (≤ 0.3 s) and fewer than 5% of views ever stall.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[1, 7, 30].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: "7px 14px",
              borderRadius: 10,
              border: `1px solid ${d === days ? GOLD : HAIRLINE}`,
              background: d === days ? GOLD : "#fff",
              color: d === days ? "#fff" : INK,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {d === 1 ? "Last 24 h" : `Last ${d} days`}
          </button>
        ))}
        {loading ? <span style={{ color: MUTED, fontSize: 12, alignSelf: "center" }}>Loading…</span> : null}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        {card("Views measured", t ? t.views.toLocaleString() : "0")}
        {card("Typical start time", ms(t && t.startP50), "half of views start faster than this")}
        {card("Instant starts", t ? `${t.instantPct}%` : "—", "started within 0.3 s", t && t.instantPct >= 75 ? GREEN : INK)}
        {card("Views with a stall", t ? `${t.stallViewsPct}%` : "—", "paused at least once to buffer", t && t.stallViewsPct > 10 ? RED : INK)}
        {card("Left before it started", t ? `${t.abandonedPct}%` : "—", "swiped away while still loading")}
      </div>

      {table("By day", data && data.byDay)}
      {table("By where the video came from", data && data.bySource, (k) => SOURCE_LABEL[k] || k)}
      {table("By phone type", data && data.byPlatform, (k) => (k === "ios" ? "iPhone" : k === "android" ? "Android" : k || "—"))}
      {table("By connection", data && data.byNetwork, (k) => (k === "wifi" ? "Wi-Fi" : k === "mobile" ? "Mobile data" : k || "—"))}
      {table("By app version", data && data.byVersion)}
      {table("By country (viewer's account)", data && data.byCountry, countryName)}
      {table("By video quality", data && data.byRendition, (k) => k || "Unknown")}
    </div>
  );
};

export default VideoPlayback;
